import { useContext, useMemo } from 'react'
import { Link } from 'react-router'
import { CheckCircleOutlined } from '@mui/icons-material'
import { Alert, Box, Grid, Typography } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'

import { AppContext } from '../../providers/app-provider'
import { getFormattedDateTime } from '../../utils/helpers'
import SimpleTable from '../simple-table'

import DeviceThumbnail from './device-thumbnail'

const columns = [
  {
    field: 'device',
    headerName: 'Devices',
    width: 400,
    sortable: false,
    renderCell: (params: GridRenderCellParams) => {
      const { deviceId, name, uniqueId, nodeTypeName } = params.row.device

      return (
        <Grid
          container={true}
          direction="row"
          sx={{
            gap: 2,
          }}
        >
          <DeviceThumbnail deviceId={deviceId} />
          <Grid
            sx={{
              flex: '1',
              paddingY: 2,
            }}
          >
            <Typography variant="body1">
              <Link to={`/tenant/${params.row.tenantId}/devices/${deviceId}`}>{name}</Link>
            </Typography>
            <Typography>{uniqueId}</Typography>
            <Typography>{nodeTypeName}</Typography>
          </Grid>
        </Grid>
      )
    },
  },
  {
    field: 'folderName',
    headerName: 'Current Folder',
    flex: 1,
    sortable: false,
    renderCell: (params: GridRenderCellParams) => (
      <Typography
        variant="body1"
        sx={{
          paddingY: 2,
          alignSelf: 'flex-start',
        }}
      >
        <Link to={`/tenant/${params.row.tenantId}/accounts/${params.row.device.folderId}`}>{params.row.folderName}</Link>
      </Typography>
    ),
  },
  {
    field: 'updated',
    headerName: 'Updated',
    minWidth: 200,
    sortable: false,
    renderCell: (params: GridRenderCellParams) => (
      <Typography
        variant="body1"
        sx={{
          paddingY: 2,
          alignSelf: 'flex-start',
        }}
      >
        {params.value ? getFormattedDateTime(params.value) : '-'}
      </Typography>
    ),
  },
  {
    field: 'status',
    headerName: 'Activated',
    renderCell: ({ row: { status } }: GridRenderCellParams) => (
      <Box
        sx={{
          paddingY: 2,
          alignSelf: 'flex-start',
        }}
      >
        {status === 'Online' || status === 'Offline' || status === 'Pause' || status === 'Extra' ? (
          <CheckCircleOutlined color="success" />
        ) : (
          <CheckCircleOutlined color="disabled" />
        )}
      </Box>
    ),
    align: 'center',
    headerAlign: 'center',
    sortable: false,
  },
] satisfies GridColDef[]

const OrderDevicesTable = ({ expandedDetails }: { expandedDetails: IOrderDetail }) => {
  const { tenantId } = useContext(AppContext)
  const { devices } = expandedDetails
  const rows = useMemo(
    () =>
      devices.map((d, idx) => ({
        id: idx,
        device: d,
        folderName: d?.folderName,
        updated: d.updatedAt,
        status: d.isActivated,
        tenantId,
      })),
    [devices, tenantId]
  )

  if (rows.length === 0)
    return (
      <Alert severity="error" sx={{ marginTop: 2, marginBottom: 1 }}>
        No devices found
      </Alert>
    )
  return <SimpleTable columns={columns} rows={rows} />
}

export default OrderDevicesTable
